import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, take } from 'rxjs';

import {
  ConfirmationModalComponent,
  ModalStandardTemplateEnum,
} from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { DataSourceFetchParams, DataSourceFetchResult } from 'src/app/services/data-source.service';
import { FiltersFactoryService } from 'src/app/services/filters-factory.service';
import { MultiSelectionListModalSections } from 'src/app/standalones/components/modals/selection-modal/multi-selection-list-modal/multi-selection-list-modal.component';
import { FilterData } from 'src/app/standalones/components/generic-filters-view/filters.interface';
import { Event } from '../../models/event';
import { AvailabilityFilterAPI, AvailabilityService } from '../../services/availability.service';
import { AvailabilityStatus, UserAvailabilityData } from '../../models/availability';
import { DateFormatEnum } from '../../pipes/dateUserLocale.pipe';

@Component({
  selector: 'app-available-event-hosts-dialog',
  templateUrl: './available-event-hosts-dialog.component.html',
  styleUrls: ['./available-event-hosts-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailableEventHostsDialogComponent implements OnInit {
  event: Event;
  DateFormatEnum = DateFormatEnum;

  @ViewChild('userHeader', { static: true }) userHeader!: TemplateRef<void>;
  @ViewChild('userCell', { static: true }) userCell!: TemplateRef<{
    $implicit: UserAvailabilityData;
  }>;
  @ViewChild('availabilityHeader', { static: true }) availabilityHeader!: TemplateRef<void>;
  @ViewChild('availabilityCell', { static: true }) availabilityCell!: TemplateRef<{
    $implicit: UserAvailabilityData;
  }>;

  columns!: MultiSelectionListModalSections<UserAvailabilityData>['columns'];
  filters: Observable<FilterData[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AvailableEventHostsDialogComponent>,
    private availabilityService: AvailabilityService,
    private translateService: TranslateService,
    private filtersFactoryService: FiltersFactoryService,
    private dialog: MatDialog,
  ) {
    this.fetch = this.fetch.bind(this);
    this.event = data.event;
    this.filters = this.filtersFactoryService.createAvailabilityFilters();
  }

  ngOnInit(): void {
    this.columns = {
      user: { size: 0.45, header: this.userHeader, cell: this.userCell },
      availability: { header: this.availabilityHeader, cell: this.availabilityCell },
    };
  }

  fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<UserAvailabilityData>> {
    const filters: AvailabilityFilterAPI = {
      ...params.filter,
      event: {
        start_time: this.event.start_time,
        end_time: this.event.end_time,
      },
    };

    return this.availabilityService
      .getAvailableTutors(filters, params.pageIndex + 1, params.pageSize)
      .pipe(
        take(1),
        map((result) => ({
          items: result.users,
          totalItems: result.totalRecords,
        })),
      );
  }

  onConfirm(usersData: UserAvailabilityData[]): void {
    const selectionContainsNonAvailableHosts = usersData.some(
      (userData) => userData.availabilityStatus !== AvailabilityStatus.AVAILABLE,
    );
    if (selectionContainsNonAvailableHosts) {
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            title: this.translateService.instant('Are you sure?'),
            description: this.translateService.instant(
              'Some organizers on this event may not be available at this time. Are you sure you want to continue?',
            ),
            confirmButtonConfig: {
              icon: 'check',
              iconType: 'mat-icon',
              label: this.translateService.instant('Confirm'),
            },
            cancelButtonConfig: {
              icon: 'close',
              iconType: 'mat-icon',
              label: this.translateService.instant('Cancel'),
            },
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((output: 'confirm' | 'cancel') => {
          if (output === 'confirm') {
            this.submitAndClose(usersData);
          }
        });
    } else {
      this.submitAndClose(usersData);
    }
  }

  onCancel(usersData: UserAvailabilityData[]): void {
    if (usersData.length) {
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            type: ModalStandardTemplateEnum.UNSAVED_CHANGES,
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((output: 'confirm' | 'cancel') => {
          if (output === 'confirm') {
            this.closeDialog();
          }
        });
    } else {
      this.closeDialog();
    }
  }

  submitAndClose(usersData: UserAvailabilityData[]): void {
    this.dialogRef.close(usersData);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
